import { Injectable, Inject } from '@angular/core';
import { Action } from '@ngrx/store';
import { CheckoutStoreFacade } from '@modules/checkout-store/facade';
import { LOGGER } from '@modules/core';
import { MsalPolicyService } from '@modules/core/services/msalPolicy.service';
import { ApiError } from '@modules/ticket-fares-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BASKET_ID_KEY } from '@nx-ng/constants';
import { LoggingService } from '@nx-ng/types';
import { updateLocalStorageItem } from '@nx-ng/util';
import { EMPTY, combineLatest, of } from 'rxjs';
import { switchMap, take, catchError } from 'rxjs/operators';
import { CompleteCheckoutActions } from '../../actions';
import {
  BasketCheckoutOperationResponse,
  BasketCheckoutRequest,
} from '../../client/src';
import { CheckoutService } from '../../services';

@Injectable()
export class CompleteCheckoutEffects {
  completeCheckoutStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompleteCheckoutActions.checkoutBasket),
      switchMap(() =>
        combineLatest([
          this.checkoutStoreFacade.checkoutBasketRequest$,
          this.checkoutStoreFacade.selectBasketId$,
        ]).pipe(
          take(1),
          switchMap(
            ([request, basketId]: [
              BasketCheckoutRequest,
              string | undefined
            ]) => {
              if (!basketId || !request) {
                return EMPTY;
              }
              return of(
                CompleteCheckoutActions.checkoutAPI_checkoutBasket({
                  request,
                  basketId,
                })
              );
            }
          )
        )
      )
    )
  );

  completeCheckoutAPI$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompleteCheckoutActions.checkoutAPI_checkoutBasket),
      switchMap((action) =>
        combineLatest([of(action), this.msalPolicyService.token$]).pipe(
          take(1),
          switchMap(
            ([action, token]: [
              { request: BasketCheckoutRequest; basketId: string } & Action,
              string | undefined
            ]) => {
              const { request, basketId } = action;
              if (!token) {
                return EMPTY;
              }
              return this.checkoutService
                .basketCheckout({
                  token,
                  basketId,
                  basketRequest: request,
                })
                .pipe(
                  switchMap((response: BasketCheckoutOperationResponse) => {
                    //Redirect to Opayo payment page
                    updateLocalStorageItem(BASKET_ID_KEY, undefined);
                    window.location.href =
                      response.opayoCheckoutInfo.paymentPageURL;
                    return of(
                      CompleteCheckoutActions.checkoutAPI_checkoutBasketSuccess(
                        {
                          response,
                        }
                      )
                    );
                  }),

                  catchError((error) => {
                    const apiError: ApiError | string =
                      error?.response?.parsedBody || error;

                    this.logger.logApiError(
                      {
                        apiError,
                      },
                      error
                    );
                    return of(
                      CompleteCheckoutActions.checkoutAPI_checkoutBasketFailure(
                        {
                          error,
                          retryAction: action,
                        }
                      )
                    );
                  })
                );
            }
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly checkoutService: CheckoutService,
    private readonly checkoutStoreFacade: CheckoutStoreFacade,
    private msalPolicyService: MsalPolicyService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}
}
